import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  Search,
  ShieldOff,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  SectionHeader,
} from '@pixlide/ui';
import { ApiError } from '@/api/client';
import {
  useDeleteUser,
  useDisableTenantUser,
  useDisableUserTwoFactor,
  useEnableTenantUser,
  usePlatformUsers,
  useSendUserPasswordReset,
} from '@/api/queries/platform-admin';
import {
  ROLE_LABELS,
  TENANT_ROLES,
  USER_STATUS_LABELS,
  USER_STATUS_TONES,
  type UserRole,
  type UserStatus,
} from '@/features/users/user-labels';
import { InviteUserForm } from '@/features/users/InviteUserForm';
import { SUPER_ADMIN_PERMISSION, useHasPermission } from '@/stores/auth-store';

const PAGE_SIZE = 20;

type PlatformUser = NonNullable<ReturnType<typeof usePlatformUsers>['data']>['items'][number];

/**
 * Utilisateurs des comptes clients, tous tenants confondus.
 *
 * C'est l'écran d'entrée du back-office pour tout ce qui touche aux personnes : le support
 * reçoit un appel avec un nom ou une adresse email, pas un identifiant de tenant. La fiche
 * d'un tenant garde sa propre table d'utilisateurs, mais elle suppose de savoir chez quel
 * client chercher — ce que cette liste n'exige pas.
 *
 * Les comptes back-office (`PlatformAdmin`) n'y figurent pas : ils se gèrent depuis
 * « Équipe », et l'API les exclut déjà de cette liste.
 */
export function UsersPage() {
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<UserRole | ''>('');
  const [status, setStatus] = useState<UserStatus | ''>('');
  const [page, setPage] = useState(1);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const users = usePlatformUsers({
    search: search.trim() === '' ? null : search.trim(),
    role: role === '' ? null : role,
    status: status === '' ? null : status,
    page,
    pageSize: PAGE_SIZE,
  });

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <SectionHeader
        title="Utilisateurs"
        description="Comptes des clients de la plateforme, tous tenants confondus."
        action={
          <Button onClick={() => setIsInviteOpen((open) => !open)}>
            <UserPlus aria-hidden className="size-4" />
            Inviter
          </Button>
        }
      />

      {isInviteOpen ? <InviteUserForm onDone={() => setIsInviteOpen(false)} /> : null}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-300"
          />
          <input
            type="search"
            aria-label="Rechercher un utilisateur"
            placeholder="Nom, email ou client…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="h-10 w-72 rounded-pill bg-white pr-4 pl-9 text-sm ring-1 ring-ink-100 placeholder:text-ink-300 focus:ring-brand-500"
          />
        </div>

        <select
          aria-label="Filtrer par rôle"
          value={role}
          onChange={(event) => {
            setRole(event.target.value === '' ? '' : (Number(event.target.value) as UserRole));
            setPage(1);
          }}
          className="h-10 rounded-pill bg-white px-4 text-sm ring-1 ring-ink-100"
        >
          <option value="">Tous les rôles</option>
          {TENANT_ROLES.map((value) => (
            <option key={value} value={value}>
              {ROLE_LABELS[value]}
            </option>
          ))}
        </select>

        <select
          aria-label="Filtrer par statut"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value === '' ? '' : (Number(event.target.value) as UserStatus));
            setPage(1);
          }}
          className="h-10 rounded-pill bg-white px-4 text-sm ring-1 ring-ink-100"
        >
          <option value="">Tous les statuts</option>
          {(Object.entries(USER_STATUS_LABELS) as [string, string][]).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        {/* Les actions sur le COMPTE CLIENT lui-même (suspendre, résilier, abonnement) vivent
            sur la fiche du tenant : ce lien garde cette partie du back-office accessible
            maintenant que la liste des tenants n'est plus dans le menu latéral. */}
        <Link
          to="/tenants"
          className="ml-auto flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900"
        >
          <Building2 aria-hidden className="size-4" />
          Comptes clients
        </Link>
      </div>

      {users.isPending ? <LoadingState label="Chargement des utilisateurs…" /> : null}

      {users.isError ? (
        <ErrorState
          message="Impossible de charger la liste des utilisateurs."
          onRetry={() => void users.refetch()}
        />
      ) : null}

      {users.data?.items.length === 0 ? (
        <EmptyState
          icon={<Users className="size-10" strokeWidth={1.25} />}
          title="Aucun utilisateur"
          description="Aucun compte ne correspond à cette recherche."
        />
      ) : null}

      {users.data && users.data.items.length > 0 ? (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">
                Liste des utilisateurs des comptes clients de la plateforme
              </caption>

              <thead className="bg-ink-50/60 text-left text-xs font-medium tracking-wide text-ink-500 uppercase">
                <tr>
                  <th scope="col" className="px-5 py-3">Utilisateur</th>
                  <th scope="col" className="px-5 py-3">Client</th>
                  <th scope="col" className="px-5 py-3">Rôle</th>
                  <th scope="col" className="px-5 py-3">Statut</th>
                  <th scope="col" className="px-5 py-3">Dernière connexion</th>
                  <th scope="col" className="px-5 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-ink-100/70">
                {users.data.items.map((user) => (
                  <UserRow key={user.id} user={user} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-ink-100/70 px-5 py-3 text-sm text-ink-500">
            <span>
              Page {users.data.page} sur {Math.max(users.data.totalPages, 1)} ·{' '}
              {users.data.totalCount} utilisateur{users.data.totalCount > 1 ? 's' : ''}
            </span>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!users.data.hasPreviousPage}
                onClick={() => setPage((current) => current - 1)}
                aria-label="Page précédente"
                className="grid size-8 place-items-center rounded-pill ring-1 ring-ink-100 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronLeft aria-hidden className="size-4" />
              </button>

              <button
                type="button"
                disabled={!users.data.hasNextPage}
                onClick={() => setPage((current) => current + 1)}
                aria-label="Page suivante"
                className="grid size-8 place-items-center rounded-pill ring-1 ring-ink-100 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronRight aria-hidden className="size-4" />
              </button>
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

/** Actions possibles sur une ligne ; `null` = aucune confirmation en attente. */
type RowAction = 'status' | 'password' | 'two-factor' | 'delete';

/**
 * Une ligne de la liste, avec ses actions — la suppression n'apparaît qu'aux super admins.
 *
 * Chacune passe par une CONFIRMATION EN DEUX TEMPS, comme sur la fiche d'un tenant : toutes
 * ont un effet immédiat côté client (une session fermée, un email envoyé, un facteur
 * d'authentification retiré) et aucune n'est annulable d'un clic. `@pixlide/ui` n'a pas de
 * composant modal, et un clic accidentel dans une liste dense est autrement vite arrivé.
 */
function UserRow({ user }: { user: PlatformUser }) {
  const disableUser = useDisableTenantUser();
  const enableUser = useEnableTenantUser();
  const sendPasswordReset = useSendUserPasswordReset();
  const disableTwoFactor = useDisableUserTwoFactor();
  const deleteUser = useDeleteUser();
  const canDelete = useHasPermission(SUPER_ADMIN_PERMISSION);

  const [confirming, setConfirming] = useState<RowAction | null>(null);

  const isDisabled = user.status === 2;

  const isMutating =
    disableUser.isPending ||
    enableUser.isPending ||
    sendPasswordReset.isPending ||
    disableTwoFactor.isPending ||
    deleteUser.isPending;

  const lastError = [
    disableUser.error,
    enableUser.error,
    sendPasswordReset.error,
    disableTwoFactor.error,
    deleteUser.error,
  ].find((error): error is ApiError => error instanceof ApiError);

  const confirmLabels: Record<RowAction, string> = {
    status: isDisabled
      ? 'Réactiver ce compte ? La personne pourra se reconnecter immédiatement.'
      : 'Désactiver ce compte ? Ses sessions en cours seront fermées.',
    password: 'Envoyer un lien de réinitialisation de mot de passe à cette adresse ?',
    'two-factor':
      'Retirer la double authentification ? Le compte ne sera plus protégé que par son mot de passe.',
    delete:
      'Supprimer définitivement ce compte ? La personne ne pourra plus se connecter et le compte disparaîtra des listes.',
  };

  const runConfirmedAction = () => {
    if (confirming === 'status') {
      const variables = { tenantId: user.tenantId, userId: user.id };

      if (isDisabled) {
        enableUser.mutate(variables);
      } else {
        disableUser.mutate(variables);
      }
    }

    if (confirming === 'password') {
      sendPasswordReset.mutate({ userId: user.id });
    }

    if (confirming === 'two-factor') {
      disableTwoFactor.mutate({ userId: user.id });
    }

    if (confirming === 'delete') {
      deleteUser.mutate({ scope: 'client', userId: user.id, tenantId: user.tenantId });
    }

    setConfirming(null);
  };

  return (
    <tr>
      <th scope="row" className="px-5 py-4 text-left font-medium">
        <span className="block">
          {user.firstName} {user.lastName}
        </span>
        <span className="block font-normal text-ink-500">{user.email}</span>
      </th>

      <td className="px-5 py-4">
        <Link
          to="/tenants/$tenantId"
          params={{ tenantId: user.tenantId }}
          className="text-ink-500 hover:text-ink-900 hover:underline"
        >
          {user.tenantName}
        </Link>
      </td>

      <td className="px-5 py-4 text-ink-500">{ROLE_LABELS[user.role]}</td>

      <td className="px-5 py-4">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={USER_STATUS_TONES[user.status]}>{USER_STATUS_LABELS[user.status]}</Badge>
          {user.twoFactorEnabled ? <Badge tone="brand">2FA</Badge> : null}
        </div>
      </td>

      <td className="px-5 py-4 text-ink-500">
        {user.lastLoginAt === null || user.lastLoginAt === undefined
          ? 'Jamais'
          : new Date(user.lastLoginAt).toLocaleDateString('fr-FR')}
      </td>

      <td className="px-5 py-4">
        {confirming !== null ? (
          <div className="flex flex-col items-end gap-2">
            <p className="max-w-xs text-right text-xs text-ink-500">{confirmLabels[confirming]}</p>

            <div className="flex gap-2">
              <Button variant="danger" size="sm" isLoading={isMutating} onClick={runConfirmedAction}>
                Confirmer
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
                Annuler
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={isMutating}
                aria-label={`Envoyer un lien de réinitialisation de mot de passe à ${user.email}`}
                onClick={() => setConfirming('password')}
              >
                <KeyRound aria-hidden className="size-4" />
              </Button>

              {user.twoFactorEnabled ? (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={isMutating}
                  aria-label={`Retirer la double authentification de ${user.email}`}
                  onClick={() => setConfirming('two-factor')}
                >
                  <ShieldOff aria-hidden className="size-4" />
                </Button>
              ) : null}

              <Button
                variant="secondary"
                size="sm"
                disabled={isMutating}
                onClick={() => setConfirming('status')}
              >
                {isDisabled ? 'Réactiver' : 'Désactiver'}
              </Button>

              {canDelete ? (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={isMutating}
                  aria-label={`Supprimer le compte de ${user.email}`}
                  onClick={() => setConfirming('delete')}
                >
                  <Trash2 aria-hidden className="size-4" />
                </Button>
              ) : null}
            </div>

            {sendPasswordReset.isSuccess ? (
              <p role="status" className="text-xs text-success-500">
                Lien de réinitialisation envoyé.
              </p>
            ) : null}

            {lastError === undefined ? null : (
              <p role="alert" className="max-w-xs text-right text-xs text-danger-500">
                {lastError.message}
              </p>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
